// Modal de upgrade de CORTESIA — 2026-08-13.
// Trigger: botão "Cortesia" no detalhe da assinatura.
//
// Diferente do UpgradeModal de propósito: aqui NÃO existe preview, porque não
// há valor a antecipar — o custo pro cliente é zero. O que o operador precisa
// ver antes de confirmar é o oposto: quanto tempo dura e por qual valor a
// assinatura volta.

import * as Dialog from '@radix-ui/react-dialog';
import { Gift, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { formatDayBR } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Select } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import { usePlans } from '../../hooks/use-plans';
import { useCourtesyUpgrade } from '../../hooks/use-subscriptions';
import { extractErrorMessage } from '../../lib/errors';
import type { SubscriptionDetail } from '../../lib/subscriptions-api';

interface CourtesyUpgradeModalProps {
  subscription: SubscriptionDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatBRL(amountString: string): string {
  const n = Number(amountString);
  if (!Number.isFinite(n)) return amountString;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function CourtesyUpgradeModal({
  subscription,
  open,
  onOpenChange,
}: CourtesyUpgradeModalProps): JSX.Element | null {
  const [toPlanId, setToPlanId] = useState<string>('');
  const courtesyMutation = useCourtesyUpgrade();
  const plansQ = usePlans({ limit: 100 });

  useEffect(() => {
    if (!open) {
      setToPlanId('');
      courtesyMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!subscription) return null;

  const precoAtual = Number(subscription.price);

  // Só planos MAIS CAROS que o preço de tabela do plano atual — o backend
  // recusa o resto com NOT_AN_UPGRADE, e oferecer opção que vai dar erro é
  // empurrar o operador pro 409.
  const precoTabelaAtual = Number(subscription.plan?.monthlyPrice ?? subscription.price);
  const planosElegiveis = (plansQ.data?.items ?? []).filter(
    (p) => p.id !== subscription.planId && Number(p.monthlyPrice) > precoTabelaAtual,
  );

  const planoEscolhido = planosElegiveis.find((p) => p.id === toPlanId);

  async function handleConfirm(): Promise<void> {
    if (!toPlanId || !subscription) return;
    try {
      const r = await courtesyMutation.mutateAsync({
        id: subscription.id,
        payload: { to_plan_id: toPlanId },
      });
      toast.success(`Cortesia concedida — volta em ${formatDayBR(r.scheduledFor)}.`);
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao conceder cortesia.'));
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[min(520px,calc(100vw-32px))]',
            '-translate-x-1/2 -translate-y-1/2 rounded-[16px]',
            'border border-line bg-surface p-6 shadow-xl',
          )}
        >
          <div className="mb-5 flex items-start justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <Gift size={18} strokeWidth={1.8} className="text-good" />
              <div>
                <Dialog.Title className="text-[15px] font-semibold text-ink">
                  Upgrade de cortesia
                </Dialog.Title>
                <Dialog.Description className="mt-0.5 text-[12px] text-muted">
                  Sobe o plano sem cobrar nada. Volta sozinho na próxima cobrança.
                </Dialog.Description>
              </div>
            </div>
            <Dialog.Close asChild>
              <button type="button" className="text-muted-2 transition hover:text-ink">
                <X size={16} strokeWidth={1.8} />
              </button>
            </Dialog.Close>
          </div>

          <Field
            label="Plano de cortesia"
            required
            hint="Só planos acima do atual — cortesia sobe, nunca desce"
          >
            {({ inputId }) => (
              <Select
                id={inputId}
                value={toPlanId}
                onChange={(e) => setToPlanId(e.target.value)}
              >
                <option value="">Selecione…</option>
                {planosElegiveis.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {formatBRL(p.monthlyPrice)}/mês
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {planosElegiveis.length === 0 && !plansQ.isLoading && (
            <p className="mt-2 text-[12px] text-muted">
              Nenhum plano acima do atual. Cortesia só sobe de plano.
            </p>
          )}

          {/* O resumo é o coração do modal: o operador precisa ver que NÃO há
              cobrança e por qual valor a assinatura volta — é justamente o que
              se perdia quando isso era feito na mão em 3 operações. */}
          {planoEscolhido && (
            <div className="mt-4 rounded-[12px] border border-line bg-surface-2 p-4">
              <dl className="space-y-2 text-[12px]">
                <div className="flex justify-between">
                  <dt className="text-muted">Cobrança agora</dt>
                  <dd className="font-medium text-good">Nenhuma — é cortesia</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted">Mensalidade do cliente</dt>
                  <dd className="font-medium text-ink">
                    {formatBRL(subscription.price)} <span className="text-muted">(não muda)</span>
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted">Volta para o plano atual em</dt>
                  <dd className="font-medium text-ink">
                    {subscription.nextBillingDate
                      ? formatDayBR(subscription.nextBillingDate)
                      : '—'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-muted">Valor após a volta</dt>
                  <dd className="font-medium text-ink">{formatBRL(subscription.price)}</dd>
                </div>
              </dl>
              <p className="mt-3 border-t border-line pt-3 text-[11px] leading-relaxed text-muted-2">
                A cortesia não perdoa cobrança em aberto: se houver fatura vencida, o corte por
                inadimplência continua valendo.
              </p>
            </div>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <PillButton type="button" onClick={() => onOpenChange(false)}>
              Cancelar
            </PillButton>
            <PillButton
              type="button"
              solid
              onClick={() => void handleConfirm()}
              disabled={!toPlanId || courtesyMutation.isPending || Number.isNaN(precoAtual)}
            >
              {courtesyMutation.isPending ? 'Concedendo…' : 'Conceder cortesia'}
            </PillButton>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
